import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { Bell, CheckCheck } from 'lucide-react';
import { formatarDiaMes, type NotificacaoDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';

const hora = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function Item({ n, abrir }: { n: NotificacaoDto; abrir: (n: NotificacaoDto) => void }) {
  return (
    <li>
      <button type="button" className={`sino__item${n.lidaEm ? '' : ' sino__item--nova'}${n.prioridade < 100 ? ' sino__item--urgente' : ''}`} onClick={() => abrir(n)}>
        <strong>{n.titulo}</strong>
        <span className="sino__motivo">{n.motivo}</span>
        <span className="sino__quando">{formatarDiaMes(n.criadaEm)}, {hora(n.criadaEm)}</span>
      </button>
    </li>
  );
}

/** Carrega ao abrir e a cada volta à aba: avisos perdidos com a conexão fora (ex.: aba em segundo plano) aparecem. */
function useCarregarAoVoltar(carregar: () => Promise<void>) {
  useEffect(() => {
    const aoVoltar = () => document.visibilityState === 'visible' && void carregar();
    aoVoltar();
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [carregar]);
}

/** Sino dos avisos (RQ-NTF-001): contador de não lidos e lista com motivo; clicar leva ao item (post-it em foco, RQ-NOT-011). */
export function Sino() {
  const { storeNotificacoes, storeEnvio, espacos } = useDependencias();
  const { naoLidas, notificacoes, carregar, marcarLidas } = useStore(storeNotificacoes);
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  useCarregarAoVoltar(carregar);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => caixa.current?.contains(e.target as Node) || setAberto(false);
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  /** Convite a aprovar (ADR-0021): no quadro atual abre a tela; em outro, vai ao quadro com o convite na URL. */
  const abrirConvite = (link: { empresaUuid: string; quadroUuid: string }, uuid: string) => {
    if (link.quadroUuid === espacos.atual.quadroUuid) return storeEnvio.getState().abrirConvite(uuid);
    window.location.assign(`/e/${link.empresaUuid}/q/${link.quadroUuid}?convite=${uuid}`);
  };

  const abrir = (n: NotificacaoDto) => {
    void marcarLidas([n.uuid]);
    setAberto(false);
    if (n.link?.conviteUuid) return abrirConvite(n.link, n.link.conviteUuid);
    if (n.link?.notaUuid) storeNotificacoes.getState().focarNota(n.link.notaUuid);
    if (n.link) espacos.navegar({ empresaUuid: n.link.empresaUuid, quadroUuid: n.link.quadroUuid, processoUuid: n.link.processoUuid ?? undefined });
  };

  return (
    <div className="sino" ref={caixa}>
      <button
        type="button"
        className="barra-superior__icone"
        aria-label={naoLidas ? `Notificações: ${naoLidas} não lidas` : 'Notificações'}
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        <Bell size={20} />
        {naoLidas > 0 && <span className="sino__contador">{naoLidas > 99 ? '99+' : naoLidas}</span>}
      </button>
      {aberto && (
        <section className="sino__painel" aria-label="Notificações">
          <header className="sino__cabecalho">
            <h2>Notificações</h2>
            {naoLidas > 0 && (
              <button type="button" className="botao-icone" onClick={() => void marcarLidas()} title="Marcar todas como lidas" aria-label="Marcar todas como lidas">
                <CheckCheck size={18} />
              </button>
            )}
          </header>
          {notificacoes.length ? (
            <ul className="sino__lista">
              {notificacoes.map((n) => <Item key={n.uuid} n={n} abrir={abrir} />)}
            </ul>
          ) : (
            <p className="sino__vazio">Nenhuma notificação por enquanto.</p>
          )}
        </section>
      )}
    </div>
  );
}
